import { useState, useEffect, useCallback } from "react";
import {
  getStrategyActivity,
  getStrategyHistory,
  getPositionSnapshots,
  getPositionSnapshotDetail,
  type ActivityLogEntry,
  type ExecutionHistoryEntry,
  type ActivityCategory,
  type PositionSnapshotSummary,
  type PositionSnapshotDetail,
} from "../../services/portfolioService";

interface StrategyHistoryProps {
  investmentId: string;
}

// Icons for different activity categories/types
const ActivityIcons = {
  // Lifecycle
  investment_created: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
    </svg>
  ),
  investment_cancelled: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  investment_liquidated: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  // Cash flow
  deposit_queued: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
    </svg>
  ),
  deposit_completed: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  withdrawal_queued: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
    </svg>
  ),
  withdrawal_completed: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  // Execution
  execution_started: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  execution_completed: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  execution_failed: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  rebalance_executed: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  market_closed: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  // Reconciliation
  drift_detected: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
  reconciliation_complete: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  ),
  // Errors
  insufficient_funds: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  symbol_delisted: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
    </svg>
  ),
  insufficient_data: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  order_rejected: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-12.728 12.728M5.636 5.636l12.728 12.728" />
    </svg>
  ),
  position_adjusted: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  // Internal transfers
  internal_transfer: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
    </svg>
  ),
  // Pending orders
  pending_order_filled: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  pending_order_cancelled: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  // Close
  investment_closed: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
    </svg>
  ),
  // Liquidation
  liquidation_started: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
    </svg>
  ),
  liquidation_completed: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  liquidation_failed: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  // Scheduled/Manual run variants
  manual_run_started: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  manual_run_completed: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  manual_run_failed: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  scheduled_run_started: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  scheduled_run_completed: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  scheduled_run_failed: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  execution_skipped: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.933 12.8a1 1 0 000-1.6L6.6 7.2A1 1 0 005 8v8a1 1 0 001.6.8l5.333-4zM19.933 12.8a1 1 0 000-1.6l-5.333-4A1 1 0 0013 8v8a1 1 0 001.6.8l5.333-4z" />
    </svg>
  ),
};

const getDefaultIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Category colors
const getCategoryStyles = (category: ActivityCategory, success?: boolean) => {
  if (success === false) {
    return {
      bg: "bg-red-500/10",
      border: "border-red-500/30",
      text: "text-red-400",
      icon: "text-red-400",
    };
  }

  switch (category) {
    case "lifecycle":
      return {
        bg: "bg-blue-500/10",
        border: "border-blue-500/30",
        text: "text-blue-400",
        icon: "text-blue-400",
      };
    case "execution":
      return {
        bg: "bg-primary-500/10",
        border: "border-primary-500/30",
        text: "text-primary-400",
        icon: "text-primary-400",
      };
    case "cash_flow":
      return {
        bg: "bg-green-500/10",
        border: "border-green-500/30",
        text: "text-green-400",
        icon: "text-green-400",
      };
    case "reconciliation":
      return {
        bg: "bg-yellow-500/10",
        border: "border-yellow-500/30",
        text: "text-yellow-400",
        icon: "text-yellow-400",
      };
    case "error":
      return {
        bg: "bg-red-500/10",
        border: "border-red-500/30",
        text: "text-red-400",
        icon: "text-red-400",
      };
    default:
      return {
        bg: "bg-surface-700/50",
        border: "border-surface-600",
        text: "text-surface-300",
        icon: "text-surface-400",
      };
  }
};

export function StrategyHistory({ investmentId }: StrategyHistoryProps) {
  const [activities, setActivities] = useState<ActivityLogEntry[]>([]);
  const [executions, setExecutions] = useState<ExecutionHistoryEntry[]>([]);
  const [snapshots, setSnapshots] = useState<PositionSnapshotSummary[]>([]);
  const [expandedActivity, setExpandedActivity] = useState<string | null>(null);
  const [expandedSnapshot, setExpandedSnapshot] = useState<string | null>(null);
  const [snapshotDetail, setSnapshotDetail] = useState<PositionSnapshotDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<ActivityCategory | "all">("all");
  const [activeTab, setActiveTab] = useState<"activity" | "snapshots">("activity");

  const loadHistory = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Load activity log, executions, and snapshots in parallel
      const [activityData, executionData, snapshotData] = await Promise.all([
        getStrategyActivity(investmentId, 60),
        getStrategyHistory(investmentId, 60),
        getPositionSnapshots(investmentId, 50).catch(() => [] as PositionSnapshotSummary[]),
      ]);
      setActivities(activityData.activities || []);
      setExecutions(executionData.executions || []);
      setSnapshots(snapshotData || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load history");
    } finally {
      setIsLoading(false);
    }
  }, [investmentId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const loadSnapshotDetail = useCallback(async (snapshotId: string) => {
    if (expandedSnapshot === snapshotId) {
      setExpandedSnapshot(null);
      setSnapshotDetail(null);
      return;
    }
    try {
      const detail = await getPositionSnapshotDetail(snapshotId);
      setSnapshotDetail(detail);
      setExpandedSnapshot(snapshotId);
    } catch {
      // Silently fail for snapshot detail
    }
  }, [expandedSnapshot]);

  // Find execution details for an activity
  const getExecutionForActivity = (activity: ActivityLogEntry): ExecutionHistoryEntry | undefined => {
    if (activity.execution_log_id) {
      return executions.find((e) => e.id === activity.execution_log_id);
    }
    // Match by timestamp for execution_completed events
    if (activity.event_type === "execution_completed" || activity.event_type === "rebalance_executed") {
      const activityTime = new Date(activity.occurred_at).getTime();
      return executions.find((e) => {
        const execTime = new Date(e.executed_at).getTime();
        return Math.abs(execTime - activityTime) < 60000; // Within 1 minute
      });
    }
    return undefined;
  };

  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  };

  const formatCurrency = (value: number | string | undefined | null) => {
    if (value === undefined || value === null) return "$0.00";
    const num = typeof value === "string" ? parseFloat(value) : value;
    if (isNaN(num)) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const filteredActivities = activeFilter === "all"
    ? activities
    : activities.filter((a) => a.category === activeFilter);

  const filterButtons: { key: ActivityCategory | "all"; label: string }[] = [
    { key: "all", label: "All" },
    { key: "execution", label: "Executions" },
    { key: "cash_flow", label: "Cash Flow" },
    { key: "lifecycle", label: "Lifecycle" },
    { key: "reconciliation", label: "Reconciliation" },
    { key: "error", label: "Errors" },
  ];

  if (isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-20 bg-surface-800 rounded-lg" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-400 mb-2">{error}</div>
        <button
          onClick={loadHistory}
          className="text-sm text-primary-400 hover:text-primary-300"
        >
          Try again
        </button>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="text-center py-8 text-surface-400">
        <svg className="w-12 h-12 mx-auto mb-3 text-surface-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p>No activity history</p>
        <p className="text-sm mt-1">Actions and executions will appear here</p>
      </div>
    );
  }

  const formatSnapshotReason = (reason: string) => {
    return reason
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  return (
    <div className="space-y-4">
      {/* Tab bar */}
      <div className="flex gap-1 border-b border-surface-700">
        <button
          onClick={() => setActiveTab("activity")}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
            activeTab === "activity"
              ? "border-primary-400 text-primary-400"
              : "border-transparent text-surface-400 hover:text-surface-300"
          }`}
        >
          Activity Timeline
        </button>
        <button
          onClick={() => setActiveTab("snapshots")}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
            activeTab === "snapshots"
              ? "border-primary-400 text-primary-400"
              : "border-transparent text-surface-400 hover:text-surface-300"
          }`}
        >
          Position Snapshots
          {snapshots.length > 0 && (
            <span className="ml-2 px-1.5 py-0.5 text-xs bg-surface-700 rounded-full">
              {snapshots.length}
            </span>
          )}
        </button>
      </div>

      {activeTab === "activity" && (
        <>
          {/* Filter buttons */}
          <div className="flex gap-2 flex-wrap">
            {filterButtons.map((btn) => (
              <button
                key={btn.key}
                onClick={() => setActiveFilter(btn.key)}
                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  activeFilter === btn.key
                    ? "bg-primary-500/20 text-primary-400 border border-primary-500/30"
                    : "bg-surface-800 text-surface-400 border border-surface-700 hover:bg-surface-700"
                }`}
              >
                {btn.label}
              </button>
            ))}
          </div>

          {/* Activity timeline */}
          <div className="space-y-2">
            {filteredActivities.map((activity) => {
              const styles = getCategoryStyles(activity.category, activity.success);
              const icon = ActivityIcons[activity.event_type as keyof typeof ActivityIcons] || getDefaultIcon();
              const isExpanded = expandedActivity === activity.id;
              const execution = getExecutionForActivity(activity);

              return (
                <div
                  key={activity.id}
                  className={`rounded-lg border ${styles.border} overflow-hidden`}
                >
                  {/* Activity Header */}
                  <button
                    onClick={() => setExpandedActivity(isExpanded ? null : activity.id)}
                    className={`w-full flex items-center justify-between p-4 hover:bg-surface-800/30 transition-colors`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${styles.bg} ${styles.icon}`}>
                        {icon}
                      </div>
                      <div className="flex flex-col items-start text-left">
                        <span className={`font-medium ${styles.text}`}>
                          {activity.title}
                        </span>
                        <span className="text-sm text-surface-400">
                          {activity.description}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-4">
                      <span className="text-sm text-surface-500">
                        {formatDate(activity.occurred_at)}
                      </span>
                      {(execution || activity.details || activity.error_message) && (
                        <svg
                          className={`w-5 h-5 text-surface-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </div>
                  </button>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-3">
                      {/* Error message */}
                      {activity.error_message && (
                        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                          <span className="font-medium">Error: </span>
                          {activity.error_message}
                        </div>
                      )}

                      {/* Execution context: type, scale factor, notes */}
                      {execution && (
                        <div className="flex flex-wrap gap-2">
                          {execution.execution_type && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface-700 text-surface-300">
                              {execution.execution_type}
                            </span>
                          )}
                          {execution.scale_factor && execution.scale_factor !== "1" && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-400/10 text-yellow-400">
                              Scale: {execution.scale_factor}
                            </span>
                          )}

                        </div>
                      )}

                      {/* Notes */}
                      {execution?.notes && (
                        <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-300 text-sm">
                          <span className="font-medium text-blue-400">Notes: </span>
                          {execution.notes}
                        </div>
                      )}

                      {/* Drift detected */}
                      {execution?.drift_detected && execution.drift_detected.length > 0 && (
                        <div className="bg-surface-900/50 rounded-lg overflow-hidden">
                          <div className="text-xs text-yellow-400 font-medium px-3 py-2 bg-yellow-500/5 border-b border-surface-700/50">
                            ⚠️ Drift Detected
                          </div>
                          <table className="w-full">
                            <thead>
                              <tr className="text-left text-xs text-surface-500 border-b border-surface-700/50">
                                <th className="py-2 px-3 font-medium">Symbol</th>
                                <th className="py-2 px-3 font-medium text-right">Shadow Qty</th>
                                <th className="py-2 px-3 font-medium text-right">Alpaca Qty</th>
                                <th className="py-2 px-3 font-medium text-right">Drift</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-surface-800">
                              {execution.drift_detected.map((drift, i) => (
                                <tr key={i} className="text-sm">
                                  <td className="py-2 px-3 text-surface-200 font-medium">{drift.symbol}</td>
                                  <td className="py-2 px-3 text-right text-surface-300">{drift.shadow_qty}</td>
                                  <td className="py-2 px-3 text-right text-surface-300">{drift.alpaca_qty}</td>
                                  <td className="py-2 px-3 text-right text-yellow-400 font-medium">{drift.drift}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Intended trades */}
                      {execution?.intended_trades && execution.intended_trades.length > 0 && (() => {
                        const trades = execution.intended_trades;
                        const hasReason = trades.some((t) => t.reason);
                        return (
                        <div className="bg-surface-900/50 rounded-lg overflow-hidden">
                          <div className="text-xs text-surface-400 font-medium px-3 py-2 bg-surface-800/50 border-b border-surface-700/50">
                            📋 Intended Trades (from strategy)
                          </div>
                          <table className="w-full">
                            <thead>
                              <tr className="text-left text-xs text-surface-500 border-b border-surface-700/50">
                                <th className="py-2 px-3 font-medium">Symbol</th>
                                <th className="py-2 px-3 font-medium">Action</th>
                                <th className="py-2 px-3 font-medium text-right">Shares</th>
                                {hasReason && (
                                  <th className="py-2 px-3 font-medium">Reason</th>
                                )}
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-surface-800">
                              {trades.map((trade, i) => (
                                <tr key={i} className="text-sm">
                                  <td className="py-2 px-3 text-surface-200 font-medium">{trade.symbol}</td>
                                  <td className="py-2 px-3">
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                      trade.action?.toUpperCase() === "BUY"
                                        ? "bg-green-400/10 text-green-400"
                                        : "bg-red-400/10 text-red-400"
                                    }`}>
                                      {trade.action?.toUpperCase()}
                                    </span>
                                  </td>
                                  <td className="py-2 px-3 text-right text-surface-300">{trade.shares}</td>
                                  {hasReason && (
                                    <td className="py-2 px-3 text-surface-400 text-xs">{trade.reason || "—"}</td>
                                  )}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        );
                      })()}

                      {/* Execution trades table */}
                      {execution && execution.orders && execution.orders.length > 0 && (
                        <div className="bg-surface-900/50 rounded-lg overflow-hidden">
                          <div className="text-xs text-surface-400 font-medium px-3 py-2 bg-surface-800/50 border-b border-surface-700/50">
                            📈 Executed Orders
                          </div>
                          <table className="w-full">
                            <thead>
                              <tr className="text-left text-xs text-surface-500 border-b border-surface-700/50">
                                <th className="py-2 px-3 font-medium">Symbol</th>
                                <th className="py-2 px-3 font-medium">Side</th>
                                <th className="py-2 px-3 font-medium text-right">Quantity</th>
                                <th className="py-2 px-3 font-medium text-right">Price</th>
                                <th className="py-2 px-3 font-medium text-right">Value</th>
                                <th className="py-2 px-3 font-medium text-center">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-surface-800">
                              {execution.orders.map((order, orderIdx) => (
                                <tr key={orderIdx} className="text-sm">
                                  <td className="py-2 px-3">
                                    <span className="font-medium text-surface-200">{order.symbol}</span>
                                  </td>
                                  <td className="py-2 px-3">
                                    <span
                                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
                                        order.side === "buy"
                                          ? "bg-green-400/10 text-green-400"
                                          : "bg-red-400/10 text-red-400"
                                      }`}
                                    >
                                      {order.side.toUpperCase()}
                                    </span>
                                  </td>
                                  <td className="py-2 px-3 text-right text-surface-300">
                                    {order.quantity.toLocaleString("en-US", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 6,
                                    })}
                                  </td>
                                  <td className="py-2 px-3 text-right text-surface-300">
                                    {formatCurrency(order.filled_price)}
                                  </td>
                                  <td className="py-2 px-3 text-right text-surface-300">
                                    {formatCurrency(order.total_value)}
                                  </td>
                                  <td className="py-2 px-3 text-center">
                                    {order.status === "failed" ? (
                                      <span
                                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-400/10 text-red-400 cursor-help"
                                        title={order.error_message || "Order failed"}
                                      >
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                        Failed
                                      </span>
                                    ) : order.status === "filled" ? (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-400/10 text-green-400">
                                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        Filled
                                      </span>
                                    ) : (
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-400/10 text-yellow-400">
                                        {order.status}
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Shadow positions before */}
                      {execution?.shadow_positions_before && execution.shadow_positions_before.length > 0 && (
                        <div className="bg-surface-900/50 rounded-lg overflow-hidden">
                          <div className="text-xs text-surface-400 font-medium px-3 py-2 bg-surface-800/50 border-b border-surface-700/50">
                            👻 Shadow Positions (Before Execution)
                          </div>
                          <table className="w-full">
                            <thead>
                              <tr className="text-left text-xs text-surface-500 border-b border-surface-700/50">
                                <th className="py-2 px-3 font-medium">Symbol</th>
                                <th className="py-2 px-3 font-medium text-right">Shares</th>
                                <th className="py-2 px-3 font-medium text-right">Cost Basis</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-surface-800">
                              {execution.shadow_positions_before.map((pos, i) => (
                                <tr key={i} className="text-sm">
                                  <td className="py-2 px-3 text-surface-200 font-medium">{pos.symbol}</td>
                                  <td className="py-2 px-3 text-right text-surface-300">{pos.shares}</td>
                                  <td className="py-2 px-3 text-right text-surface-300">{formatCurrency(pos.cost_basis)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Additional details from the details JSON (for non-execution events) */}
                      {activity.details && Object.keys(activity.details).length > 0 && !execution && (
                        <div className="bg-surface-900/50 rounded-lg p-3">
                          <div className="text-xs text-surface-500 mb-2">Details</div>
                          <div className="space-y-1 text-sm">
                            {Object.entries(activity.details).map(([key, value]) => (
                              <div key={key} className="flex justify-between">
                                <span className="text-surface-400 capitalize">
                                  {key.replace(/_/g, " ")}
                                </span>
                                <span className="text-surface-200">
                                  {typeof value === "number"
                                    ? value.toLocaleString()
                                    : String(value)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {activeTab === "snapshots" && (
        <div className="space-y-2">
          {snapshots.length === 0 ? (
            <div className="text-center py-8 text-surface-400">
              <svg className="w-12 h-12 mx-auto mb-3 text-surface-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p>No position snapshots yet</p>
              <p className="text-sm mt-1">Snapshots are taken automatically before position changes</p>
            </div>
          ) : (
            snapshots.map((snapshot) => {
              const isExpanded = expandedSnapshot === snapshot.id;
              return (
                <div key={snapshot.id} className="rounded-lg border border-surface-700 overflow-hidden">
                  <button
                    onClick={() => loadSnapshotDetail(snapshot.id)}
                    className="w-full flex items-center justify-between p-4 hover:bg-surface-800/30 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-purple-500/10 text-purple-400">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div className="flex flex-col items-start text-left">
                        <span className="font-medium text-purple-400">
                          {formatSnapshotReason(snapshot.reason)}
                        </span>
                        <span className="text-sm text-surface-400">
                          {snapshot.reason_detail || `${snapshot.item_count} position${snapshot.item_count !== 1 ? "s" : ""} captured`}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface-700 text-surface-300">
                        {snapshot.item_count} positions
                      </span>
                      <span className="text-sm text-surface-500">
                        {formatDate(snapshot.snapshot_at)}
                      </span>
                      <svg
                        className={`w-5 h-5 text-surface-400 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </button>

                  {isExpanded && snapshotDetail && snapshotDetail.id === snapshot.id && (
                    <div className="px-4 pb-4">
                      {snapshotDetail.items && snapshotDetail.items.length > 0 ? (
                        <div className="bg-surface-900/50 rounded-lg overflow-hidden">
                          <table className="w-full">
                            <thead>
                              <tr className="text-left text-xs text-surface-500 border-b border-surface-700/50">
                                <th className="py-2 px-3 font-medium">Symbol</th>
                                <th className="py-2 px-3 font-medium text-right">Shares</th>
                                <th className="py-2 px-3 font-medium text-right">Cost Basis</th>
                                <th className="py-2 px-3 font-medium text-right">Mkt Price</th>
                                <th className="py-2 px-3 font-medium text-right">Mkt Value</th>
                                <th className="py-2 px-3 font-medium">Status</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-surface-800">
                              {snapshotDetail.items.map((item) => (
                                <tr key={item.id} className="text-sm">
                                  <td className="py-2 px-3 text-surface-200 font-medium">{item.symbol}</td>
                                  <td className="py-2 px-3 text-right text-surface-300">{item.shares}</td>
                                  <td className="py-2 px-3 text-right text-surface-300">{formatCurrency(item.cost_basis)}</td>
                                  <td className="py-2 px-3 text-right text-surface-300">
                                    {item.market_price ? formatCurrency(item.market_price) : "—"}
                                  </td>
                                  <td className="py-2 px-3 text-right text-surface-300">
                                    {item.market_value ? formatCurrency(item.market_value) : "—"}
                                  </td>
                                  <td className="py-2 px-3">
                                    {item.fill_status && (
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                        item.fill_status === "confirmed"
                                          ? "bg-green-400/10 text-green-400"
                                          : "bg-yellow-400/10 text-yellow-400"
                                      }`}>
                                        {item.fill_status}
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {/* Cash/deposit info from first item */}
                          {snapshotDetail.items[0]?.investment_cash_balance && (
                            <div className="px-3 py-2 border-t border-surface-700/50 flex gap-4 text-xs text-surface-400">
                              <span>Cash: {formatCurrency(snapshotDetail.items[0].investment_cash_balance)}</span>
                              {snapshotDetail.items[0].investment_net_deposits && (
                                <span>Net Deposits: {formatCurrency(snapshotDetail.items[0].investment_net_deposits)}</span>
                              )}
                              {snapshotDetail.items[0].investment_pending_delta && snapshotDetail.items[0].investment_pending_delta !== "0" && (
                                <span>Pending Delta: {formatCurrency(snapshotDetail.items[0].investment_pending_delta)}</span>
                              )}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-sm text-surface-400 text-center py-4">
                          No position items in this snapshot (empty portfolio)
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
